/**
 * @file use-plan-generated-popup.ts
 * @module app/modules/orders-delivery/hooks/use-plan-generated-popup
 * @description Hook for displaying a success popup when a plan is generated for a vehicle.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 2.1.1
 * @version 2.1.2
 */

import { useEffect, useRef } from 'react';
import Popup from '../../_common/components/pop-up';

interface UsePlanGeneratedPopupParams {
  isOpen: boolean;
  vehicleName?: string;
  routeRefKey?: string;
  onDismiss: () => void;
}

export const usePlanGeneratedPopup = ({
  isOpen,
  vehicleName,
  routeRefKey,
  onDismiss,
}: UsePlanGeneratedPopupParams) => {
  const onDismissRef = useRef(onDismiss);

  useEffect(() => {
    onDismissRef.current = onDismiss;
  }, [onDismiss]);

  useEffect(() => {
    if (!isOpen) return;
    let active = true;
    const vehicleLabel = vehicleName || 'selected vehicle';
    const routeReference = routeRefKey ? ` (${routeRefKey})` : '';
    void Popup.success({
      title: 'Plan generated successfully',
      message: `Plan for vehicle ${vehicleLabel}${routeReference} has been generated successfully!`,
      confirmButtonText: 'Okay',
    }).then(() => {
      if (active) onDismissRef.current();
    });

    return () => {
      active = false;
      Popup.close();
    };
  }, [isOpen, vehicleName, routeRefKey]);
};